import React, { ReactElement, useState, useMemo, useEffect } from 'react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { AxiosSingular } from '@deleteagency/axios-singular';
import axios from 'axios';
import { nanoid } from 'nanoid';
import { api } from 'project/general/js/api';
import { getUrlByParams } from 'project/general/js/url-helper';
import { historyController } from 'project/general/js/history-controller';
import { PagedData } from 'models/paged-data';
import { PaginationModel } from 'models/pagination-model';
import { clone, removeEmptyKeys } from 'project/general/js/object-helper';

const DEFAULT_PAGINATION = {
    page: 1,
    pageCount: 0,
    pageSize: 12,
    totalCount: 0,
    nextUrl: '',
};

type PlainHtml = string;

export type AvailableItemTypes = PlainHtml;

export interface ContextContent<T> {
    isLoading: boolean;

    isFailedLoading: boolean;

    items: T[];

    setFilter(options: AvailableOptions, add?: boolean): void;

    clearFilters(): void;

    appliedFilters: AvailableOptions;

    pagination: PaginationModel;

    isOpenMobileFilter: boolean;

    loadMoreBtn: boolean | undefined;

    itemsCount: number | undefined;
}

interface AvailableOptions {
    [key: string]: string[] | string | number | boolean;
}

export const FilterContext = React.createContext({} as ContextContent<AvailableItemTypes>);
const request = new AxiosSingular(api, axios.CancelToken, axios.isCancel);

export const FilterService = ({
    children,
    endpoint,
    requiredParams = {},
    options = {},
    startCollection = [],
    takeFirstFromApi = false,
    startLoadMore,
    itemsNum,
}: iFilterService): ReactElement => {
    const id = useMemo(() => nanoid(10), []);
    const [filterState, setState] = useState({
        isFailedLoading: false,
        isLoading: takeFirstFromApi,
        appliedFilters: options,
        items: startCollection,
        pagination: DEFAULT_PAGINATION as PaginationModel,
        isOpenMobileFilter: false,
        loadMoreBtn: startLoadMore,
        itemsCount: itemsNum,
    });
    const {
        isLoading,
        appliedFilters,
        items,
        isFailedLoading,
        pagination,
        isOpenMobileFilter,
        loadMoreBtn,
        itemsCount,
    } = filterState;
    const _sendRequest = (
        _options: AvailableOptions,
        ignoreOther?: boolean,
        add?: boolean
    ): void => {
        const newFilters = ignoreOther
            ? Object.assign(_options, requiredParams)
            : { ...appliedFilters, ..._options, ...requiredParams };
        const oldFilters = clone(appliedFilters);
        removeEmptyKeys(newFilters);
        setState({
            ...filterState,
            isLoading: true,
            appliedFilters: newFilters,
            loadMoreBtn: false,
        });
        request
            .get(getUrlByParams(endpoint, newFilters))
            .then(({ data }: { data: PagedData<AvailableItemTypes> }) => {
                const staticPagination = {
                    page: Number(data.nextUrl) - 1,
                    pageCount: data.count,
                    pageSize: 12,
                    totalCount: Number(data.foundPosts),
                    nextUrl: data.nextUrl,
                    currentPosts: data.currentPosts,
                };
                setState({
                    ...filterState,
                    items: add ? [...items, ...[data.html]] : [data.html],
                    isLoading: false,
                    isFailedLoading: false,
                    appliedFilters: newFilters,
                    // pagination: data.pagination,
                    pagination: staticPagination,
                    loadMoreBtn: false,
                    itemsCount: data.count,
                });
                historyController.replaceState(
                    id,
                    '',
                    getUrlByParams(window.location.origin + window.location.pathname, newFilters)
                );
            })
            .catch((error: any) => {
                setState({
                    ...filterState,
                    isLoading: false,
                    isFailedLoading: true,
                    appliedFilters: oldFilters,
                    loadMoreBtn: false,
                });
                console.dir(error);
            });
    };
    const setFilter = (_options: AvailableOptions, add = false): void => {
        if (isLoading) request.cancel();
        if (!_options.page) _options.page = '';
        _sendRequest(_options, false, add);
    };
    const clearFilters = (): void => {
        if (isLoading) request.cancel();
        _sendRequest(requiredParams, true, false);
    };
    useEffect(() => {
        if (takeFirstFromApi) _sendRequest(options, false, false);
    }, []);
    return (
        <FilterContext.Provider
            value={{
                isLoading,
                isFailedLoading,
                items,
                appliedFilters,
                pagination,
                isOpenMobileFilter,
                loadMoreBtn,
                itemsCount,
                setFilter,
                clearFilters,
            }}
        >
            {children}
        </FilterContext.Provider>
    );
};

interface iFilterService {
    children?: ReactElement | ReactElement[];
    endpoint: string;
    options?: AvailableOptions;
    startCollection?: any[];
    takeFirstFromApi?: boolean;
    requiredParams?: AvailableOptions;
    startLoadMore?: boolean | undefined;
    isOpenMobileFilter?: boolean;
    itemsNum?: number | undefined;
}
